package com.exura.pages.publicplans


import androidx.compose.runtime.*
import com.exura.components.FlexContent
import com.exura.components.buttons.AppButton
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonDangerVariant
import com.exura.components.buttons.ButtonGhostVariant
import com.exura.components.buttons.ButtonOutlineVariant
import com.exura.components.buttons.ButtonPrimaryVariant
import com.exura.components.dialogs.DialogPanelLargeVariant
import com.exura.components.dialogs.QrCodeDialogContent
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraEmphasisedTextVariant
import com.exura.components.pageTypography.ExuraLinkTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralCardEntry
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.GhostCardEntryVariant
import com.exura.components.pageTypography.HighlightedCardEntryVariant
import com.exura.components.pageTypography.InteractiveCardEntryVariant
import com.exura.components.pageTypography.OptionsSectionStyle
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageOptionsSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.SelectedListEntryVariant
import com.exura.components.pageTypography.TitledSection
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.components.pageTypography.WithTooltip
import com.exura.features.localization.commonLocalization
import com.exura.features.plandefinition.domain.model.PlanContentSummary
import com.exura.features.plandefinition.planLocalization
import com.exura.features.plandefinition.presentation.PlanDefinitionDetails
import com.exura.features.plandefinition.presentation.PlanDefinitionListSelectionDialogContent
import com.exura.features.plandefinition.presentation.PlanStatisticElement
import com.exura.features.publicplans.domain.model.AnonymousPublicPlanOrder
import com.exura.features.publicplans.domain.model.ProviderPublicPlan
import com.exura.features.publicplans.domain.model.PublicPlanServingEntry
import com.exura.features.publicplans.domain.model.PublicPlanStatistics
import com.exura.features.publicplans.publicPlansLocalization
import com.exura.ui.Dimens
import com.exura.navigation.Route
import com.exura.pages.publicplans.orders.PublicPlanOrdersPreview
import com.exura.pages.publicplans.widgets.PublicPlanSharingEnabledWidget
import com.exura.ui.Assets
import com.exura.util.formatLocalDate
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.JustifyContent

@Page(Route.WithParams.PUBLIC_PLAN_DETAILS)
@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.PublicPlanDetailsPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val planId = context.route.params[Route.Params.PUBLIC_PLAN_ENTRY_ID]
    LaunchedEffect(planId) {
        if (planId.isNullOrBlank()) {
            context.router.navigateTo(Route.PUBLIC_PLAN)
        }
    }
    if (planId.isNullOrBlank()) return


    val controller = rememberPublicPlanDetailsController(scope, context, planId)
    val state = controller.uiState

    val onReplaceClicked = {
        showDialog(DialogPanelLargeVariant) {
            PlanDefinitionListSelectionDialogContent(
                onPlanSelected = { versionedPlan ->
                    versionedPlan.firebaseDocId?.let { newSourceId ->
                        val event = PublicPlanDetailsEvent.ReplacePlan(newSourceId)
                        controller.handleEvent(event)
                    }
                }
            )
        }
    }

    val onQrClicked = {
        showDialog { QrCodeDialogContent(data = state.plan?.qrCodeUri ?: "") }
    }

    when {
        state.isLoading && state.plan == null -> Loader()
        state.error != null -> PageErrorContent(errorMessage = state.error)
        state.plan != null -> PublicPlanDetailsView(
            isPageWorking = state.isLoading,
            plan = state.plan,
            orders = state.orders,
            servings = state.servings,
            onReplaceClick = onReplaceClicked,
            onQrClick = onQrClicked,
            onEvent = controller::handleEvent,
        )
    }

}

@Composable
private fun PublicPlanDetailsView(
    isPageWorking: Boolean,
    plan: ProviderPublicPlan,
    orders: List<AnonymousPublicPlanOrder>,
    servings: List<PublicPlanServingEntry>,
    onReplaceClick: () -> Unit,
    onQrClick: () -> Unit,
    onEvent: (PublicPlanDetailsEvent) -> Unit,
) {
    Column(modifier = Modifier.fillMaxSize()) {

        PageNavigationHeaderSection(
            publicPlansLocalization.current.publicPlans,
            plan.accessCode,
            isPageWorking = isPageWorking,
            onBack = { onEvent(PublicPlanDetailsEvent.GoBack) },
        )
        PageMainContent(modifier = Modifier.weight(1f)) {
            Column(modifier = Modifier.fillMaxSize()) {
                PublicPlanDetailsOptionsSection(
                    isSharingEnabled = plan.isSharingEnabled,
                    onRefreshStats = { onEvent(PublicPlanDetailsEvent.RefreshStats) },
                    onToggleSharing = { onEvent(PublicPlanDetailsEvent.ToggleSharing) },
                    onReplacePlan = onReplaceClick
                )
                PublicShareEntryAccessOptions(plan, onQrClick = onQrClick, modifier = OptionsSectionStyle.toModifier())
                PublicPlanDetailsViewContent(
                    modifier = Modifier.weight(1f),
                    plan = plan,
                    orders = orders,
                    servings = servings,
                )
            }
        }

    }
}

@Composable
private fun PublicPlanDetailsViewContent(
    modifier: Modifier = Modifier,
    plan: ProviderPublicPlan,
    orders: List<AnonymousPublicPlanOrder>,
    servings: List<PublicPlanServingEntry>,
) = PageSecondaryContent(modifier) {
    Column(
        modifier = Modifier
            .fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(Dimens.pageSectionDefaultGap)
    ) {
        PublicPlanStatisticsRibbon(stats = plan.statistics)
        CurrentPublicPlanSummaryCardWidget(summary = plan.summary)
        TitledSection(title = publicPlansLocalization.current.servingHistory) { PublicPlanServingHistory(servings = servings) }
        TitledSection(title = publicPlansLocalization.current.planUsage) { PublicPlanOrdersPreview(orders = orders) }
    }
}

@Composable
private fun PublicPlanStatisticsRibbon(stats: PublicPlanStatistics) {
    Row(modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant).fillMaxWidth()) {
        SharingDetailsCard(Modifier.weight(1f)) {
            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.views(stats.viewCount),
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_VIEWS,
            ) { SpanText(stats.viewCount.toString()) }

        }
        SharingDetailsCard(Modifier.weight(1f)) {
            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.lastView,
                iconSrc = Assets.IconsUntiled.GENERAL_DATE,
            ) {
                SpanText(
                    stats.lastViewDate?.let {d -> formatLocalDate(d, isWithHours = true)}
                        ?: publicPlansLocalization.current.planStatistics.neverViewed
                )
            }
        }
        SharingDetailsCard(Modifier.weight(1f)) {
            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.accesses(stats.accessCount),
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ACCESSES,
            ) { SpanText(stats.accessCount.toString()) }
        }
        SharingDetailsCard(Modifier.weight(1f)) {
            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.lastAccess,
                iconSrc = Assets.IconsUntiled.GENERAL_DATE,
            ) {
                SpanText(
                    stats.lastAccessedDate?.let {d -> formatLocalDate(d, isWithHours = true)}
                        ?: publicPlansLocalization.current.planStatistics.neverAccessed)
            }
        }
    }
}


@Composable
private fun PublicPlanDetailsOptionsSection(
    isSharingEnabled: Boolean,
    onRefreshStats: suspend () -> Unit, // Suspend for AsyncButton
    onToggleSharing: suspend () -> Unit,
    onReplacePlan: () -> Unit,
) = PageOptionsSection {
    AsyncButton(
        text = if (isSharingEnabled) publicPlansLocalization.current.lockSharing else publicPlansLocalization.current.lockSharingUnlock,
        onClick = onToggleSharing,
        variant = if (isSharingEnabled) ButtonDangerVariant else ButtonPrimaryVariant,
        icon = {
            UntiledIcon(
                if (isSharingEnabled) Assets.IconsUntiled.PUBLIC_PLAN_DISABLE_SHARING
                else Assets.IconsUntiled.PUBLIC_PLAN_ENABLE_SHARING
            )
        }
    )
    AsyncButton(
        text = publicPlansLocalization.current.refreshStatistics,
        onClick = onRefreshStats,
        variant = ButtonOutlineVariant,
        icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_REFRESH) }
    )
    AppButton(
        text = publicPlansLocalization.current.swapTemplate,
        onClick = onReplacePlan,
        variant = ButtonGhostVariant,
        icon = { UntiledIcon(Assets.IconsUntiled.PUBLIC_PLAN_SWAP_PLAN_DEFINITION) }
    )
}

@Composable
private fun PublicShareEntryAccessOptions(
    plan: ProviderPublicPlan,
    onQrClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(Dimens.cardEntriesBaseGap),
        modifier = Modifier.fillMaxWidth().then(modifier),
    ) {
        PublicPlanAccessCode(
            code = plan.accessCode,
            isSharingEnabled = plan.isSharingEnabled,
            modifier = Modifier.weight(1f),
        )
        PublicPlanWebsiteLink(
            link = plan.websiteLink,
            modifier = Modifier.weight(1f),
        )
        PublicPlanQrCode(
            modifier = Modifier.weight(1f).onClick { onQrClick() },
        )
    }
}

@Composable
private fun PublicPlanAccessCode(
    code: String,
    isSharingEnabled: Boolean,
    modifier: Modifier = Modifier,
) {
    SharingDetailsCard(modifier) {
        Column(
            verticalArrangement = Arrangement.spacedBy(Dimens.cardEntryTopBottomSpacer),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            UntiledIcon(
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ACCESS_CODE,
                modifier = Modifier.fontSize(Dimens.mainIconSizeDefault),
            )
            PublicPlanSharingEnabledWidget(code, isSharingEnabled)
        }
    }
}

@Composable
private fun PublicPlanWebsiteLink(
    link: String,
    modifier: Modifier = Modifier,
) {
    var showCopiedFeedback by remember { mutableStateOf(false) }

    SharingDetailsCard(modifier) {
        Column(
            verticalArrangement = Arrangement.spacedBy(Dimens.cardEntryTopBottomSpacer),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            WithTooltip(link) {
                UntiledIcon(
                    iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ACCESS_LINK,
                    modifier = Modifier.fontSize(Dimens.mainIconSizeDefault),
                )
            }
            UnderwrittenText(
                topVariant = ExuraBodyHeaderTextVariant,
                underwrite = { SpanText(publicPlansLocalization.current.publicPlansPage) }
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Dimens.cardEntryLeftRightSpacer),
                ) {
                    WithTooltip(
                        if (showCopiedFeedback) commonLocalization.current.controls.copyLinkCopied
                        else commonLocalization.current.controls.copyLink
                    ) {
                        UntiledIcon(
                            iconSrc = if (showCopiedFeedback) Assets.IconsUntiled.CHECK_MARK else Assets.IconsUntiled.COPY_CONTENT,
                            modifier = ExuraTextStyle.toModifier(ExuraLinkTextVariant)
                                .onClick {
                                    window.navigator.clipboard.writeText(link)
                                    @Suppress("AssignedValueIsNeverRead")
                                    showCopiedFeedback = true
                                },
                        )
                    }
                    WithTooltip(commonLocalization.current.controls.openInNewTab) {
                        UntiledIcon(
                            iconSrc = Assets.IconsUntiled.OPEN_LINK,
                            modifier = ExuraTextStyle.toModifier(ExuraLinkTextVariant)
                                .onClick { window.open(link, "_blank") },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PublicPlanQrCode(
    modifier: Modifier = Modifier,
) {
    SharingDetailsCard(
        modifier = modifier.cursor(Cursor.ZoomIn),
        isInteractive = true,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(Dimens.cardEntryTopBottomSpacer),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            UntiledIcon(
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ACCESS_URI,
                modifier = Modifier.fontSize(Dimens.mainIconSizeDefault),
            )
            UnderwrittenText(
                topVariant = ExuraBodyHeaderTextVariant,
                underwriteVariant = ExuraCommentaryTextVariant,
                underwrite = { SpanText(commonLocalization.current.controls.clickToEnlarge) }
            ) {
                SpanText(
                    publicPlansLocalization.current.qr.qrCode,
                    modifier = ExuraTextStyle.toModifier(ExuraEmphasisedTextVariant)
                )
            }
        }
    }
}

@Composable
private fun SharingDetailsCard(
    modifier: Modifier = Modifier,
    isInteractive: Boolean = false,
    content: @Composable () -> Unit,
) = GeneralCardEntry(
    modifier = modifier,
    variant = GhostCardEntryVariant.let { variant ->
        if (isInteractive) variant.then(InteractiveCardEntryVariant)
        else variant
    },
    content = content,
)

@Composable
private fun PublicPlanServingHistory(
    modifier: Modifier = Modifier,
    servings: List<PublicPlanServingEntry>,
) {
    Column(modifier = Modifier.fillMaxWidth().then(modifier)) {
        servings
            .sortedByDescending { it.sharedFrom }
            .forEachIndexed { ind, serving ->
                PublicPlanServingEntryEntry(
                    entry = serving,
                    isSelected = (ind == 0),
                )
            }
    }
}


@Composable
private fun PublicPlanServingEntryEntry(
    modifier: Modifier = Modifier,
    entry: PublicPlanServingEntry,
    isSelected: Boolean,
) = GeneralListEntry(
    modifier = modifier,
    variant = if (isSelected) SelectedListEntryVariant else null,
) {
    val commCount = entry.summary.numberFloating +
            entry.summary.numberQuestion +
            entry.summary.numberPersistent +
            entry.summary.numberInstruction

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        FlexContent(
            weight = 7,
            justifyContent = JustifyContent.Start,
        ) {
            UnderwrittenText(
                topVariant = ExuraBodyHeaderTextVariant,
                underwriteVariant = ExuraCommentaryTextVariant,
                underwrite = { SpanText("ID: ${entry.sharedPlanId}") }
            ) { SpanText(entry.summary.title) }
        }
        FlexContent(
            weight = 1,
            justifyContent = JustifyContent.Start,
        ) {
            PlanStatisticElement(
                underwriteText = planLocalization.current.metadata.duration,
                iconSrc = Assets.IconsUntiled.DURATION,
            ) { SpanText(planLocalization.current.daysCount(entry.summary.planDuration)) }
        }
        FlexContent(
            weight = 1,
            justifyContent = JustifyContent.Start,
        ) {
            PlanStatisticElement(
                underwriteText = planLocalization.current.series.communiques(commCount),
                iconSrc = Assets.IconsUntiled.COMMUNICATES_ALL,
            ) { SpanText(commCount.toString()) }
        }

        FlexContent(
            weight = 2,
            justifyContent = JustifyContent.End,
        ) {
            UnderwrittenText(
                underwriteVariant = ExuraCommentaryTextVariant,
                underwrite = { SpanText(publicPlansLocalization.current.planSharing.availableFrom) },
            ) {
                InlineIcon(icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_DATE) }) {
                    SpanText(formatLocalDate(entry.sharedFrom, isWithHours = true))
                }
            }
        }
    }
}

@Composable
private fun CurrentPublicPlanSummaryCardWidget(summary: PlanContentSummary) {
    GeneralCardEntry(
        modifier = Modifier.fillMaxWidth(),
        variant = HighlightedCardEntryVariant,
    ) {
        PlanDefinitionDetails(
            modifier = Modifier.fillMaxWidth(),
            title = summary.title,
            description = summary.description,
            versionId = null,
            planDuration = summary.planDuration,
            numberQuestion = summary.numberQuestion,
            numberFloating = summary.numberFloating,
            numberPersistent = summary.numberPersistent,
            numberInstruction = summary.numberInstruction,
        )
    }
}
